<template>
  <ProductDetailTopLayout>
    <template #media>
      <ProductDetailImages
        :product-enrichments="productEnrichments"
        :show-experimental-function="showExperimentalFunction"
      />
    </template>
    <template #data>
      <ProductDetailHeadSkeleton v-if="store.loading" />
      <template v-else>
        <div
          v-if="
            store.decoratedProduct?.isDiscontinued || pendingDiscontinuation
          "
          :class="$style.productHint"
        >
          <AlertBox
            icon="attention"
            :text="
              store.decoratedProduct?.isDiscontinued
                ? htmlDictionary['pdp.hint.discontinued'] ??
                  '<b>Hint:</b> This product is no longer on sale. This page is archived.'
                : htmlDictionary['pdp.hint.pendingDiscontinuation'] ??
                  '<b>Hint:</b> Upcoming discontinuation.'
            "
            :style="'warning'"
          />
        </div>
        <ProductDetailHeader
          :id="store.decoratedProduct?.name ?? ''"
          :name="store.decoratedProduct?.family ?? ''"
          :variant-name="store.decoratedProduct?.modelName ?? ''"
          :description="
            !store.decoratedProduct?.isDiscontinued &&
            store.decoratedProduct?.productDescription
              ? store.decoratedProduct?.productDescription
              : ''
          "
          :attributes="
            !store.decoratedProduct?.isDiscontinued
              ? store.highlightedSpecs
              : []
          "
          :special-badge="store.decoratedProduct?.buildBadgeText()"
          :is-loading="store.loading"
        />

        <div
          v-if="
            !store.decoratedProduct?.isDiscontinued &&
            store.decoratedProduct?.productTeaserText
          "
          :class="[
            $style['teaser-text-renderer'],
            'product-detail product-detail__description'
          ]"
          v-html="store.decoratedProduct?.productTeaserText"
        />
        <ProductDetailFollowingProductLinks
          v-if="store.decoratedProduct?.isDiscontinued"
        />

        <ProductDetailConfigureOptions />
        <ProductDetailProductAddons />
        <ProductDetailCrosssellingProductAddons />
        <ProductDetailAddToCart />
      </template>
    </template>
  </ProductDetailTopLayout>

  <slot name="contentSlotAboveTabs"></slot>
  <TabsSection
    id="product-detail-page-tabs"
    :key="tabs.map(({ title }) => title).join('---')"
    :tab-list-full-width="false"
  >
    <template #panels>
      <!--
      TabsSection uses TabsComponent which uses a TransitionGroup.
      A TransitionGroup requires a v-for for correct rendering.

      @see https://vuejs.org/guide/built-ins/transition-group.html#differences-from-transition
      @see https://github.com/vuejs/core/issues/6745
      @see https://gcp.baslerweb.com/jira/browse/WEB2-1852
      @see https://gcp.baslerweb.com/jira/browse/WEB2-1984
      -->
      <TabPanel
        v-for="tab in tabs"
        :key="tab.title"
        :uid="tab.id"
        :active-on-init="tab.active"
        :title="tab.title"
      >
        <slot
          v-if="tab.component === LibProductEnrichmentTabElement"
          name="contentSlotTabElement"
          v-bind="tab.props"
        ></slot>
        <component :is="tab.component" v-else v-bind="tab.props" />
      </TabPanel>
    </template>
    <template
      v-if="
        store.decoratedProduct?.sku &&
        groups.length !== 0 &&
        !store.decoratedProduct?.isDiscontinued
      "
      #tab-list-additional-content
    >
      <a
        id="tab-list-suitable-products"
        class="button button--no-icon button--link button--medium"
        href="#suitable-products"
      >
        {{
          $textDictionary['pdp.relatedproducts.link'] ?? 'See Suitable Products'
        }}
      </a>
    </template>
  </TabsSection>

  <slot name="contentSlotCenter"></slot>

  <NuxtErrorBoundary @error="handleRelatedProductsError">
    <LazyVisibleShopRelatedProducts
      v-if="
        !store.decoratedProduct?.isDiscontinued &&
        store.decoratedProduct?.hasRelatedProducts
      "
      hydrate-on-visible
      :generate-detail-link="createProductDetailLink"
    />
    <!--
      XXX: do we show an error or simply omit the section?
    <template #error="error">
      There was an error loading related products.
    </template>
    -->
  </NuxtErrorBoundary>
</template>

<script setup lang="ts">
import {
  AlertBox,
  ProductDetailHeader,
  ProductDetailTopLayout,
  TabPanel,
  TabsSection
} from '@hypercodestudio/basler-components';
import type { MediaImageInterface } from '@hypercodestudio/basler-components/dist/components/elements/media/types/MediaImage.vue';
import ProductDetailTabFeature from './ProductDetailTabFeatures.vue';
import ProductDetailTabDocuments from './ProductDetailTabDocuments.vue';
import LibProductEnrichmentTabElement from '~/components/global/lib/ProductEnrichmentTabElement.vue';
import ProductDetailTabSpecifications from './ProductDetailTabSpecifications.vue';
import { LazyShopRelatedProducts } from '#components';
import ProductDetailHeadSkeleton from './skeletons/ProductDetailHeadSkeleton.vue';
import ProductDetailConfigureOptions from './ProductDetailConfigureOptions.vue';
import ProductDetailAddToCart from './ProductDetailAddToCart.vue';
import { useProductDetailPageStore } from '~/stores/productDetailPageStore/productDetailPageStore';
import { createEcommerceTrackingData } from '~/stores/cartStore/helper/createEcommerceTrackingData';
import { useCreateProductDetailLink } from '~/composables/useCreateProductDetailLink';
import { isDefined } from '~/utils/guards/isDefined';
import type { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import ProductDetailProductAddons from '~/components/shop/ProductDetailProductAddons.vue';
import ProductDetailFollowingProductLinks from '~/components/shop/ProductDetailFollowingProductLinks.vue';
import type { IProductEnrichment } from '~/lib/ContentfulService';
import { useDownloadStore } from '~/stores/downloadStore/downloadStore';
import ProductDetailDistributors from '~/components/shop/ProductDetailDistributors.vue';
import { isConfigurableProduct } from '~/stores/productDetailPageStore/helper/isConfigurableProduct';
import ProductDetailImages from '~/components/shop/ProductDetailImages.vue';
import type { GalleryItem } from '@hypercodestudio/basler-components/dist/components/modules/ProductDetailGallery.vue';
import { getProductImage } from '~/utils/shop/getProductImage';
import ProductDetailCrosssellingProductAddons from '~/components/shop/ProductDetailCrosssellingProductAddons.vue';
import { ToastMessageType } from '~/composables/useShowToast';

type TypeContentModel = {
  id: string;
  title: string;
  props?: Record<string, unknown>;
  component: any;
  visible?: boolean;
  active?: boolean;
};

interface Props {
  generatePdpLink: (locale: string, product: DecoratedProduct) => string;
  companyName?: string | null;
  additionalTabContent?: TypeContentModel[];
  productEnrichments: IProductEnrichment[];
}

declare global {
  interface Window {
    baslerIsDiscontinued: boolean;
  }
}

const props = withDefaults(defineProps<Props>(), {
  additionalTabContent: () => [],
  companyName: null,
  productEnrichments: () => []
});

const {
  public: { showExperimentalFunction }
} = useRuntimeConfig();

const locale = useRouteParamLocale();
const logger = useLogger();
const route = useRoute();
const router = useRouter();
const store = useProductDetailPageStore();
const pdpConfiguration = usePdpConfiguration();
const { $textDictionary, $analytics, $locale, $globalPageSettings } =
  useNuxtApp();
const htmlDictionary = useHTMLDictionary([
  'pdp.hint.discontinued',
  'pdp.hint.pendingDiscontinuation'
]);
const toasts = useShowToast();
const createProductDetailLink = useCreateProductDetailLink();

const tabTitle = ref();
const tabId = ref();
const tabPosition = ref();
const pendingDiscontinuation = computed(() => {
  return (
    store.decoratedProduct?.discontinuationStatus === 'Pending discontinuation'
  );
});

const hasProductFeatureAttributes = computed(
  () => (store.decoratedProduct?.renderFeatureAttributes() ?? []).length > 0
);

const hasGeneralTab = computed(() =>
  props.productEnrichments.some(
    (enrichment) =>
      enrichment?.fields?.contentSlotTabElement &&
      enrichment.fields.contentSlotTabElement.length > 0
  )
);

const generalTabActive = computed(() =>
  props.productEnrichments.some(
    (enrichment) =>
      enrichment?.fields?.contentSlotTabElement &&
      enrichment.fields.contentSlotTabElement.length > 0 &&
      enrichment.fields?.contentSlotTabPosition === 1
  )
);

const generalTabTitle = computed(() => {
  props.productEnrichments.forEach((element) => {
    if (element?.fields?.contentSlotTabTitle && !tabTitle.value) {
      tabTitle.value = element.fields.contentSlotTabTitle;
    }
  });
  return (
    tabTitle.value ??
    $textDictionary.value['pdp.general.tabPanel.title.label'] ??
    'General'
  );
});

const generalTabId = computed(() => {
  props.productEnrichments.forEach((element) => {
    if (element?.sys?.id && !tabId.value) {
      tabId.value = 'tab-product-enrichment' + element.sys.id;
    }
  });
  return tabId.value ?? 'tab-general';
});

const generalTabPosition = computed(() => {
  props.productEnrichments.forEach((element) => {
    if (element?.fields?.contentSlotTabPosition && !tabPosition.value) {
      tabPosition.value = element.fields.contentSlotTabPosition;
    }
  });
  return tabPosition.value ?? 1;
});

const downloadStore = useDownloadStore();
const downloadTabVisible = ref(false);

watch(
  () => store.decoratedProduct?.sku,
  async () => {
    if (store.decoratedProduct?.sku) {
      downloadTabVisible.value = await downloadStore.checkForExistingDownloads(
        store.decoratedProduct.sku
      );
    } else {
      downloadTabVisible.value = false;
    }
  },
  { immediate: true }
);

const tabs = computed<TypeContentModel[]>(() =>
  [
    {
      pos: generalTabPosition.value,
      id: generalTabId.value,
      title: generalTabTitle.value,
      component: LibProductEnrichmentTabElement,
      visible: hasGeneralTab.value,
      active: generalTabActive.value
    },
    {
      pos: 1,
      id: 'tab-specifications',
      title:
        $textDictionary.value['pdp.specifications.tabPanel.title.label'] ??
        'Specification Title',
      component: ProductDetailTabSpecifications,
      visible: true,
      active: !generalTabActive.value
    },
    {
      pos: 2,
      id: 'tab-features',
      title:
        $textDictionary.value['pdp.feature.tabPanel.title.label'] ??
        'Feature Tab',
      component: ProductDetailTabFeature,
      visible:
        !store.decoratedProduct?.isDiscontinued &&
        hasProductFeatureAttributes.value
    },
    {
      pos: 3,
      id: 'tab-documents',
      title:
        $textDictionary.value['pdp.documents.tabPanel.title.label'] ??
        'Documents',
      component: ProductDetailTabDocuments,
      visible: downloadTabVisible.value
    },
    {
      pos: 4,
      id: 'tab-sources-of-supply',
      title:
        $textDictionary.value['pdp.sources-of-supply.tabPanel.title.label'] ??
        'Sources of supply',
      component: ProductDetailDistributors,
      visible:
        store.decoratedProduct?.sku !== '' &&
        store.decoratedProduct?.channelPartnersData &&
        store.decoratedProduct.channelPartnersData.length > 0
    }
  ]
    .filter(({ visible }) => visible)
    .sort((a, b) => a.pos - b.pos)
);

const seoMetaTitle = computed(() => {
  const productMetaTitle = store.decoratedProduct?.metaTitle;
  const modelName = store.decoratedProduct?.modelName;
  const companyName = props.companyName ?? '';

  if (productMetaTitle && productMetaTitle.length > 0) {
    return `${productMetaTitle} | ${companyName}`;
  } else if (modelName && modelName.length > 0) {
    return `${modelName} | ${companyName}`;
  }

  return companyName;
});

const req = useRequestURL();
const hostname = useHostname();

const canonical = computed(() => {
  const copiedUrl = new URL(hostname);
  copiedUrl.pathname = req.pathname;
  copiedUrl.search = '';
  copiedUrl.hash = '';
  return copiedUrl.toString();
});

const seoProductImageUrl = computed(() => {
  let ogImageUrl = (
    store.decoratedProduct?.getProductImage(
      {
        aspectRatio: [4, 3],
        format: 'jpg'
      },
      $globalPageSettings.value?.defaultProductImage ?? undefined
    )?.content as MediaImageInterface
  )?.src;
  if (isDefined(ogImageUrl) && ogImageUrl?.startsWith('//')) {
    ogImageUrl = `https:${ogImageUrl}`;
  }
  return ogImageUrl;
});

const seoRobots = computed(() => {
  // magento currently returns only the labels and no technical stable value,
  // so we map the displayed label to the correct value.
  const indexMap: Record<string, string> = {
    'no-index': 'noindex',
    index: 'index'
  };
  const followMap: Record<string, string> = {
    'no-follow': 'nofollow',
    follow: 'follow'
  };

  return (
    [
      indexMap[store.decoratedProduct?.seoIndex?.toLowerCase() ?? ''],
      followMap[store.decoratedProduct?.seoFollow?.toLowerCase() ?? '']
    ]
      .filter(isDefined)
      .join(', ') || undefined
  );
});

const groups = computed(() => {
  if (!pdpConfiguration.value) {
    return [];
  }

  return (
    store.decoratedProduct
      ?.getGroupedRelatedProducts(pdpConfiguration.value)
      .filter((item) => item.products.length > 0) ?? []
  );
});

useSeoMeta({
  title: () => seoMetaTitle.value,
  ogTitle: () => seoMetaTitle.value,
  description: () => store.decoratedProduct?.metaDescription ?? undefined,
  ogDescription: () => store.decoratedProduct?.metaDescription ?? undefined,
  ogUrl: canonical,
  ogImage: () => seoProductImageUrl.value ?? undefined,
  robots: () => seoRobots.value
});

useHead({
  link: [
    {
      rel: 'canonical',
      href: canonical
    }
  ]
});

watch(
  () => store.decoratedProduct,
  () => {
    if (store.decoratedProduct) {
      $analytics?.pushToDataLayer({
        event: 'view_item',
        ecommerce: createEcommerceTrackingData({
          cartItems: [
            {
              decoratedProduct: store.decoratedProduct,
              quantity: 1
            }
          ]
        })
      });
    }
  },
  { immediate: true }
);

watch(
  () => store.error,
  () => {
    if (store.error) {
      toasts.showToast($textDictionary.value[store.error] ?? store.error, {
        severity: ToastMessageType.ERROR
      });
    }
  }
);

useHead(() => {
  if (!store.decoratedProduct) {
    return {};
  }

  const offer = {
    '@type': 'Offer',
    price: store.decoratedProduct.price ?? undefined,
    priceCurrency: store.decoratedProduct.productCurrency ?? undefined,
    availability: store.decoratedProduct.showShippingTime
      ? 'InStock'
      : 'PreOrder'
  };

  const productImages = computed<GalleryItem[]>(() => {
    if (!store.decoratedProduct) {
      return [];
    }

    return getProductImage(
      store.decoratedProduct.product,
      {
        width: 2400,
        height: 1800,
        widths: [400, 800, 1600, 2400],
        aspectRatio: [4, 3]
      },
      $globalPageSettings.value?.defaultProductImage ?? undefined
    );
  });

  const jsonLdData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    sku: store.decoratedProduct.sku ?? undefined,
    name: store.decoratedProduct.modelName ?? undefined,
    description: store.decoratedProduct.productDescription ?? undefined,
    image: productImages.value.map((x) => x.media.content.src),
    url: props.generatePdpLink(locale.value, store.decoratedProduct) ?? '',
    offers: offer
  };

  return {
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(jsonLdData),
        tagPriority: -1 // Optional: Sicherstellen, dass es zuletzt eingefügt wird
      }
    ]
  };
});

function handleRelatedProductsError(args: unknown) {
  logger.error(args);
}

onMounted(() => {
  store.loading = false;

  window.baslerIsDiscontinued = store.decoratedProduct?.isDiscontinued ?? false;

  if (isConfigurableProduct(store.decoratedProduct?.product)) {
    // XXX: Fallback: Normally a page redirect occurs caused by magento configuration, in case it is missing, this code will display the cheapest variant instead of the ConfigurableProduct (parent product)
    const cheapestVariant = store.decoratedProduct?.product?.variants?.reduce(
      (prev, curr) => {
        const previousPrice =
          prev?.product?.price_range?.minimum_price?.final_price?.value;
        const currentPrice =
          curr?.product?.price_range?.minimum_price?.final_price?.value;

        if (isDefined(previousPrice) && isDefined(currentPrice)) {
          return previousPrice < currentPrice ? prev : curr;
        }

        if (!isDefined(previousPrice) && !isDefined(currentPrice)) {
          return null;
        }
        if (!isDefined(previousPrice) && isDefined(currentPrice)) {
          return curr;
        }
        if (isDefined(previousPrice) && !isDefined(currentPrice)) {
          return prev;
        }

        return null;
      }
    );

    if (cheapestVariant?.product?.url_key) {
      router.push({
        path: `/${convertContentfulToWebappLocaleCode($locale.value)}/shop/${
          cheapestVariant?.product?.url_key
        }`,
        query: route.query
      });
    }
  }
});
</script>

<style module>
.productHint {
  margin-bottom: 20px;
}

.productHint p {
  color: white;
}

.teaser-text-renderer p {
  color: inherit;
}

.teaser-text-renderer ul {
  list-style: disc;
  padding: revert;
  margin-block-end: var(--spacing-size-small);
}
</style>
